import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import {
  deleteTreatmentRecord,
  paginateTreatmentRecords,
  TreatmentRecordPaginationPayload,
  deleteTreatmentVisual,
} from "../../api/treatmentRecord";

// Interface for patient treatment records
interface PatientTreatmentRecord {
  id: string;
  doctorId: string;
  patientId: string;
  clinicId: string;
  treatmentId: string;
  wireChange: string;
  treatmentNotes: string;
  upperJawOption: string | null;
  lowerJawOption: string | null;
  isAligner: boolean;
  createdOn: string;
  updatedOn: string;
  treatmentVisuals: Array<{
    id: string;
    treatmentRecordId: string;
    url: string;
    createdOn: string;
  }>;
  patient: {
    id: string;
    uniqueCode: string;
    name: string;
    gender: string;
    dob: string;
    contact: string;
    email: string;
    profilePictureUrl: string;
  };
  clinic: {
    id: string;
    name: string;
    address: string;
    contact: string;
  };
  treatment: {
    id: string;
    treatmentObjective: string;
    jawType: string;
    dentalAndIntraOralExamination: string;
  };
}

const defaultPayload: TreatmentRecordPaginationPayload = {
  curPage: 1,
  perPage: 10,
  sortBy: "createdOn",
  direction: "desc",
  whereClause: [],
};

const TreatmentRecordList: React.FC = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [payload, setPayload] =
    useState<TreatmentRecordPaginationPayload>(defaultPayload);
  const lastFetchKeyRef = useRef<string>("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [showVisualConfirm, setShowVisualConfirm] = useState(false);
  const [selectedVisualId, setSelectedVisualId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});

  // Patient records modal state
  const [isPatientRecordsModalOpen, setIsPatientRecordsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<PatientTreatmentRecord | null>(null);
  const [patientRecordsLoading, setPatientRecordsLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / payload.perPage));

  const getTreatmentTypeLabel = (rawType: string | undefined | null) => {
    if (!rawType) return "—";
    const upper = String(rawType).toUpperCase();
    if (upper === "EXTRACTION") return "Extraction";
    if (upper === "NON_EXTRACTION") return "No Extraction";
    return rawType;
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await paginateTreatmentRecords(payload);
      // API returns { data: [...], total: ..., count: ... }
      const dataArray = res?.data?.data || [];
      setItems(Array.isArray(dataArray) ? dataArray : []);
      setTotal(res?.data?.total || res?.data?.count || 0);
    } catch (err) {
      toast.error("Failed to load treatment records");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Show specific record details
  const showRecordDetails = (record: any) => {
    setSelectedRecord(record);
    setIsPatientRecordsModalOpen(true);
  };

  useEffect(() => {
    const key = `${payload.curPage}|${payload.perPage}|${payload.sortBy}|${payload.direction}|${JSON.stringify(
      payload.whereClause || []
    )}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payload.curPage,
    payload.perPage,
    payload.sortBy,
    payload.direction,
    payload.whereClause,
  ]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the open menu and button
      const isClickOnButton = Object.values(buttonRefs.current).some(
        (btn) => btn && btn.contains(target)
      );
      const isClickOnMenu = target.closest('.dropdown-menu-container');

      if (!isClickOnButton && !isClickOnMenu && openMenuId) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const goToPage = (p: number) => {
    setPayload((prev) => ({
      ...prev,
      curPage: Math.min(Math.max(1, p), totalPages),
    }));
  };

  const changePerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10) || 10;
    setPayload((prev) => ({ ...prev, perPage: value, curPage: 1 }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPayload((prev) => ({
      ...prev,
      curPage: 1,
      whereClause: value
        ? [
          {
            key: "patientName",
            value,
            operator: "=",
          },
        ]
        : [],
    }));
  };

  const handleConfirmDelete = async (id: string) => {
    if (!id) {
      toast.error("Unable to delete: missing record id.");
      return;
    }
    try {
      setLoading(true);
      await deleteTreatmentRecord(id);
      setShowConfirm(false);
      setShowSuccess(true);
      fetchItems();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to delete treatment record."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVisual = async (visualId: string) => {
    try {
      setLoading(true);
      await deleteTreatmentVisual(visualId);
      toast.success("Treatment visual deleted successfully");
      setShowVisualConfirm(false);
      setSelectedVisualId("");
      if (selectedRecord) {
        setSelectedRecord({
          ...selectedRecord,
          treatmentVisuals: selectedRecord.treatmentVisuals.filter((v: any) => v.id !== visualId)
        });
      }
      fetchItems();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to delete treatment visual."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              {/* Header Row with Search and New button */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Treatment Records
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Click on any row to view detailed information for that specific treatment record
                  </p>
                </div>
                <div className="flex flex-col md:flex-row md:items-center md:gap-4">

                  <button
                    className="mt-3 md:mt-0 px-4 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                    onClick={() => navigate("/treatment-record/create")}
                  >
                    + New Treatment Record
                  </button>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-600">
                  Total: <span className="font-medium">{total}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-full md:w-64">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search by patient name..."
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <span className="text-sm text-gray-600">Rows:</span>
                  <select
                    className="border rounded-lg p-1 select-plain"
                    value={payload.perPage}
                    onChange={changePerPage}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Patient Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Clinic Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Treatment Type
                      </th>

                      {/* <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={5}
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      items.map((rec: any) => {
                        const recordId =
                          rec.id ||
                          rec._id ||
                          rec.treatmentRecordId ||
                          rec.recordId ||
                          rec.value ||
                          "";
                        return (
                          <tr
                            key={recordId}
                            className="border-t hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => showRecordDetails(rec)}
                          >
                            <td className="px-4 py-3 text-sm text-gray-800">
                              {rec.treatment?.patient?.name ||
                                rec.patient?.name ||
                                rec.patientName ||
                                rec.patientId ||
                                "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {rec.treatment?.Clinic?.name ||
                                rec.treatment?.clinic?.name ||
                                rec.clinic?.name ||
                                rec.clinicName ||
                                rec.clinicId ||
                                "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {getTreatmentTypeLabel(
                                rec.treatment?.treatmentType ||
                                rec.treatmentType ||
                                rec.treatmentId
                              )}
                            </td>

                            {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div 
                                className="relative" 
                                ref={(el) => {
                                  if (openMenuId === recordId) {
                                    (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                  }
                                }}
                              >
                                <button
                                  ref={(el) => {
                                    if (el) {
                                      buttonRefs.current[recordId] = el;
                                    }
                                  }}
                                  className="text-gray-600 hover:text-gray-900 focus:outline-none p-1 rounded-full hover:bg-gray-100"
                                  onClick={(e) => {
                                    if (openMenuId === recordId) {
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    } else {
                                      const button = e.currentTarget;
                                      const rect = button.getBoundingClientRect();
                                      setDropdownPosition({
                                        top: rect.bottom + window.scrollY + 4,
                                        right: window.innerWidth - rect.right,
                                      });
                                      setOpenMenuId(recordId);
                                    }
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle cx="12" cy="5" r="2" />
                                    <circle cx="12" cy="12" r="2" />
                                    <circle cx="12" cy="19" r="2" />
                                  </svg>
                                </button>
                                {openMenuId === recordId && dropdownPosition && createPortal(
                                  <div 
                                    className="dropdown-menu-container fixed w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden"
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    right: `${dropdownPosition.right}px`,
                                  }}
                                  >
                                    <button
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                      onClick={() => {
                                        navigate(`/treatment-record/create/${recordId}`);
                                        setOpenMenuId(null);
                                        setDropdownPosition(null);
                                      }}
                                    >
                                      Edit
                                    </button>
                                    <div className="border-t border-gray-200"></div>
                                    <button
                                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                      onClick={() => {
                                        setSelectedId(recordId);
                                        setShowConfirm(true);
                                        setOpenMenuId(null);
                                        setDropdownPosition(null);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>,
                                  document.body
                                )}
                              </div>
                            </td> */}
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <hr />
              {/* Pagination */}
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Page {payload.curPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(1)}
                    disabled={payload.curPage === 1}
                  >
                    « First
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage - 1)}
                    disabled={payload.curPage === 1}
                  >
                    ‹ Prev
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage + 1)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(totalPages)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Last »
                  </button>
                </div>
              </div>

              <ConfirmModal
                isOpen={showConfirm}
                title="Delete Treatment Record"
                message="Are you sure you want to delete this treatment record?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={() => handleConfirmDelete(selectedId)}
                onCancel={() => setShowConfirm(false)}
                loading={loading}
              />
              <ConfirmModal
                isOpen={showVisualConfirm}
                title="Delete Treatment Visual"
                message="Are you sure you want to delete this visual?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={() => handleDeleteVisual(selectedVisualId)}
                onCancel={() => {
                  setShowVisualConfirm(false);
                  setSelectedVisualId("");
                }}
                loading={loading}
                zIndex={100}
              />
              <SuccessModal
                isOpen={showSuccess}
                title="Treatment Record Deleted"
                message="The treatment record has been deleted successfully."
                onClose={() => setShowSuccess(false)}
              />

              {/* Patient Records Modal */}
              {isPatientRecordsModalOpen && selectedRecord && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                  <div
                    className="absolute inset-0 bg-black/50"
                    onClick={() => setIsPatientRecordsModalOpen(false)}
                  />
                  <div className="relative z-10 w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden mx-4">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        {selectedRecord.patient?.profilePictureUrl && (
                          <img
                            src={`https://m6.microlent.com/api/${selectedRecord.patient.profilePictureUrl}`}
                            alt="Patient"
                            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <h3 className="text-xl font-semibold text-gray-800 truncate">
                            {selectedRecord.patient?.name || "Patient"} - Treatment Record
                          </h3>
                          <p className="text-sm text-gray-600 truncate">
                            {selectedRecord.patient?.uniqueCode} • {selectedRecord.patient?.gender} • {new Date(selectedRecord.createdOn).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setIsPatientRecordsModalOpen(false)}
                        className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 flex-shrink-0"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Modal Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                      <div className="border border-gray-200 rounded-xl p-6 bg-gray-50 w-full overflow-hidden">
                        {/* Record Header */}
                        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                          <div className="flex items-center gap-3">
                            <span className={`text-sm font-medium px-3 py-1 rounded-full ${selectedRecord.isAligner
                                ? 'bg-green-100 text-green-800'
                                : 'bg-purple-100 text-purple-800'
                              }`}>
                              {selectedRecord.isAligner ? 'Aligner Treatment' : 'Braces Treatment'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 truncate">
                            Clinic: {selectedRecord.clinic?.name || 'N/A'}
                          </div>
                        </div>

                        {/* Record Details */}
                        <div className="space-y-6 mb-6">
                          {!selectedRecord.isAligner && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div>
                                <label className="text-sm font-medium text-gray-700 block mb-2">Upper Jaw Option</label>
                                <div className="bg-white p-3 rounded-lg border">
                                  <p className="text-gray-800">{selectedRecord.upperJawOption || 'Not specified'}</p>
                                </div>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700 block mb-2">Lower Jaw Option</label>
                                <div className="bg-white p-3 rounded-lg border">
                                  <p className="text-gray-800">{selectedRecord.lowerJawOption || 'Not specified'}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Treatment Notes - Full Width */}
                          <div>
                            <label className="text-sm font-medium text-gray-700 block mb-2">Treatment Notes</label>
                            <div className="bg-white p-4 rounded-lg border min-h-[120px] w-full overflow-hidden">
                              <p className="text-gray-800 whitespace-pre-wrap leading-relaxed break-words word-wrap overflow-wrap-anywhere">{selectedRecord.treatmentNotes || 'No notes provided'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Treatment Visuals */}
                        {selectedRecord.treatmentVisuals && selectedRecord.treatmentVisuals.length > 0 && (
                          <div>
                            <label className="text-sm font-medium text-gray-700 block mb-4">
                              Treatment Visuals ({selectedRecord.treatmentVisuals.length})
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {selectedRecord.treatmentVisuals.map((visual, visualIndex) => (
                                <div key={visual.id} className="relative group">
                                  <img
                                    src={visual.url}
                                    alt={`Treatment visual ${visualIndex + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border border-gray-200 group-hover:opacity-75 transition-opacity cursor-pointer"
                                    onClick={() => window.open(visual.url, '_blank')}
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-30 rounded-lg pointer-events-none">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                    </svg>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedVisualId(visual.id);
                                      setShowVisualConfirm(true);
                                    }}
                                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-all z-10"
                                    title="Delete Image"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* No visuals message */}
                        {(!selectedRecord.treatmentVisuals || selectedRecord.treatmentVisuals.length === 0) && (
                          <div className="text-center py-8 bg-white rounded-lg border border-dashed border-gray-300">
                            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-gray-500">No treatment visuals available for this record</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default TreatmentRecordList;


